import { useState } from 'react';
import { Alert, Button, Card, Eyebrow, Select, formatDate, money, useConfirm, type ConfirmRequest } from '@busbook/ui';
import { Page } from '../components/Shell.js';
import { Column, DataTable, Drawer, StatusBadge } from '../components/DataTable.js';
import { errorMessage, useResource } from '../lib/useResource.js';
import { api } from '../lib/api.js';
import { useT } from '../i18n/index.js';

/** Cross-tenant invoice ledger (docs/07 "/invoices"). */

interface LineItem {
  kind: string;
  description: string;
  amountCents: number;
}

interface InvoiceRow {
  id: string;
  number: string;
  periodStart: string;
  periodEnd: string;
  subtotalCents: number;
  totalCents: number;
  currency: string;
  status: string;
  issuedAt: string | null;
  paidAt: string | null;
  lineItems: LineItem[];
  tenant: { name: string; slug: string };
}

export function Invoices() {
  const t = useT();
  const [status, setStatus] = useState('');
  const params = new URLSearchParams({ pageSize: '100' });
  if (status) params.set('status', status);
  const invoices = useResource<InvoiceRow[]>(`/platform/invoices?${params.toString()}`);
  const [selected, setSelected] = useState<InvoiceRow | null>(null);

  const columns: Column<InvoiceRow>[] = [
    { key: 'number', header: 'Invoice', render: (row) => <span className="tabular font-medium">{row.number}</span> },
    { key: 'tenant', header: 'Tenant', render: (row) => row.tenant.name },
    {
      key: 'period',
      header: 'Period',
      render: (row) => (
        <span className="tabular">
          {formatDate(row.periodStart, 'UTC')} – {formatDate(row.periodEnd, 'UTC')}
        </span>
      ),
    },
    { key: 'total', header: 'Total', numeric: true, render: (row) => money(row.totalCents, row.currency) },
    { key: 'status', header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
  ];

  return (
    <Page
      title={t('invoices.title')}
      description={t('invoices.subtitleLong')}
      actions={
        <Select
          aria-label={t('common.filterStatus')}
          className="h-9 w-40"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">{t('common.allStatuses')}</option>
          <option value="DRAFT">Draft</option>
          <option value="SENT">Sent</option>
          <option value="PAID">Paid</option>
          <option value="VOID">Void</option>
        </Select>
      }
    >
      <DataTable
        columns={columns}
        rows={invoices.data}
        rowKey={(row) => row.id}
        loading={invoices.loading}
        error={invoices.error}
        onRowClick={(row) => setSelected(row)}
        empty={{
          title: 'No invoices',
          description: 'Invoices are drafted at the start of each month for the month before.',
        }}
      />

      {selected && (
        <InvoiceDrawer
          invoice={selected}
          onClose={() => setSelected(null)}
          onChanged={async () => {
            await invoices.reload();
            setSelected(null);
          }}
        />
      )}
    </Page>
  );
}

function InvoiceDrawer({
  invoice,
  onClose,
  onChanged,
}: {
  invoice: InvoiceRow;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { ask, dialog } = useConfirm();

  async function transition(action: 'send' | 'mark-paid' | 'void', confirm?: ConfirmRequest) {
    if (confirm && !(await ask(confirm))) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/platform/invoices/${invoice.id}/${action}`, {});
      await onChanged();
    } catch (err) {
      setError(errorMessage(err, 'Could not update this invoice.'));
    } finally {
      setBusy(false);
    }
  }

  const lines = Array.isArray(invoice.lineItems) ? invoice.lineItems : [];

  return (
    <Drawer open title={invoice.number} onClose={onClose}>
      <div className="flex flex-col gap-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-base font-semibold">{invoice.tenant.name}</p>
            <p className="text-sm tabular" style={{ color: 'var(--color-muted)' }}>
              {formatDate(invoice.periodStart, 'UTC')} – {formatDate(invoice.periodEnd, 'UTC')}
            </p>
          </div>
          <StatusBadge status={invoice.status} />
        </div>

        <Card>
          <Eyebrow>Line items</Eyebrow>
          <table className="mt-2 w-full text-sm">
            <tbody>
              {lines.map((line, index) => (
                <tr key={index}>
                  <td className="py-1.5" style={{ color: 'var(--color-muted)' }}>
                    {line.description}
                  </td>
                  <td className="py-1.5 text-right tabular font-medium">
                    {money(line.amountCents, invoice.currency)}
                  </td>
                </tr>
              ))}
              <tr>
                <td className="border-t pt-2 font-semibold" style={{ borderColor: 'var(--color-line)' }}>
                  Total
                </td>
                <td
                  className="border-t pt-2 text-right tabular font-semibold"
                  style={{ borderColor: 'var(--color-line)' }}
                >
                  {money(invoice.totalCents, invoice.currency)}
                </td>
              </tr>
            </tbody>
          </table>
        </Card>

        {error && <Alert tone="error">{error}</Alert>}

        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            loading={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api.download(
                  `/platform/invoices/${invoice.id}/pdf`,
                  `${invoice.number}.pdf`,
                );
              } catch {
                setError('Could not download that invoice.');
              } finally {
                setBusy(false);
              }
            }}
          >
            Download PDF
          </Button>
          {invoice.status === 'DRAFT' && (
            <Button
              loading={busy}
              onClick={() =>
                transition('send', {
                  title: `Send ${invoice.number} to ${invoice.tenant.name}?`,
                  body: 'It becomes visible to them as issued.',
                  confirmLabel: 'Send to tenant',
                  destructive: false,
                })
              }
            >
              Send to tenant
            </Button>
          )}
          {invoice.status === 'SENT' && (
            <Button
              loading={busy}
              onClick={() =>
                transition('mark-paid', {
                  title: `Mark ${invoice.number} as paid?`,
                  body: 'Record this once the money has arrived.',
                  confirmLabel: 'Mark as paid',
                  destructive: false,
                })
              }
            >
              Mark as paid
            </Button>
          )}
          {(invoice.status === 'DRAFT' || invoice.status === 'SENT') && (
            <Button
              variant="secondary"
              loading={busy}
              onClick={() =>
                transition('void', {
                  title: `Void ${invoice.number}?`,
                  body: 'This cannot be undone.',
                  confirmLabel: 'Void invoice',
                })
              }
            >
              Void
            </Button>
          )}
          {invoice.status === 'PAID' && (
            <p className="text-sm" style={{ color: 'var(--signal-departed)' }}>
              Paid{invoice.paidAt ? ` on ${formatDate(invoice.paidAt, 'UTC')}` : ''}.
            </p>
          )}
        </div>
      </div>
      {dialog}
    </Drawer>
  );
}
